// ── LES CHEMINS D'EXCEPTION DE L'ESPACE SÉJOUR ──────────────────────────────
//
// Module PUR. Dix chemins qui existaient déjà dans le code mais que le dessin
// n'avait pas prévus, plus un onzième qui n'existait nulle part : la carte
// refusée. Puis un douzième [D32] : le délai de paiement écoulé (48 h après
// signature), qui partageait à tort l'écran « Lien expiré » alors que le
// parcours n'y est PAS mort — le contrat signé reste valable.
//
// ═══ LES TEXTES SONT REPRIS VERBATIM ═══
// Arbitrage Dimitri (2026-09-04) : « reprends les textes exacts de l'existant,
// ils sont bons ». Chaque entrée ci-dessous porte l'endroit d'où elle vient.
// Ce ne sont pas des reformulations : ce sont les mêmes chaînes, déplacées.
// Si l'un de ces textes doit changer, il change ICI et nulle part ailleurs —
// c'est justement ce que le regroupement achète.
//
// ═══ CE QU'UN ÉCRAN D'EXCEPTION DOIT PORTER ═══ [D27]
// « Chacun dit ce qui s'est passé, ce que ça change pour le voyageur, et porte
// un geste. Jamais un cul-de-sac. »
//   · `titre`   — ce qui s'est passé
//   · `message` — ce que ça change
//   · `suite`   — ce qu'on peut faire (texte)
//   · `geste`   — le bouton, ou `null` quand le seul recours est de nous écrire
//                 (le pied de page porte alors « Écrivez-nous », toujours
//                 présent : aucun écran ne se termine sans issue)
//
// ═══ POURQUOI CE MODULE EXISTE ═══
// Ces onze situations vivaient dispersées : trois dans une table d'erreurs,
// trois dans une table d'états terminaux, cinq dans les branches d'un
// gestionnaire de clic, et la onzième nulle part. Un chemin qu'on ne dessine
// pas est un chemin qu'on supprime — c'est exactement ce qui est arrivé au
// téléchargement du contrat le jour où un écran a pris la tête sans reprendre
// son lien. Les rassembler, c'est pouvoir les compter.

"use strict";

/** Le ton gouverne la couleur du bandeau, jamais le fond de l'écran. */
const TON_MAUVAIS = "mauvais"; // c'est arrivé, il faut faire autrement
const TON_ATTENTE = "attente"; // c'est temporaire, réessayer suffit

/**
 * Les douze chemins.
 *
 * `geste.type` :
 *   'ecrire'   → le pied de page suffit, aucun bouton plein (sinon on promet un
 *                geste qu'on ne tient pas)
 *   'refaire'  → « Refaire une demande » — le SEUL rattrapage commercial d'une
 *                demande annulée, et le serveur calcule pour lui la
 *                disponibilité réelle des dates. À ne jamais perdre.
 *   'reessayer'→ un bouton qui relance l'action, sur place
 *   'recharger'→ la page se recharge d'elle-même
 */
const CHEMINS = {
  // ── 1 · Lien inconnu ── verbatim src/JourneyPage.jsx (table `state==='error'`)
  lien_inconnu: {
    titre: "Lien introuvable",
    message:
      "Ce lien ne correspond à aucun séjour. Vérifiez le lien reçu par email.",
    ton: TON_MAUVAIS,
    geste: { type: "ecrire" },
  },

  // ── 2 · Lien invalide (mal formé) ── verbatim, même table
  lien_invalide: {
    titre: "Lien invalide",
    message:
      "Ce lien est invalide. Vérifiez le lien reçu par email.",
    ton: TON_MAUVAIS,
    geste: { type: "ecrire" },
  },

  // ── 3 · Panne ── verbatim, même table
  panne: {
    titre: "Erreur",
    message:
      "Impossible de charger votre séjour pour le moment. Réessayez dans quelques instants.",
    ton: TON_ATTENTE,
    geste: { type: "reessayer", libelle: "Réessayer" },
  },

  // ── 4 · Demande non retenue ── verbatim TERMINAL_MESSAGES.rejected
  demande_non_retenue: {
    titre: "Demande non retenue",
    message:
      "Votre demande de réservation n'a pas pu être confirmée.",
    ton: TON_MAUVAIS,
    geste: { type: "ecrire" },
  },

  // ── 5 · Lien de signature expiré (72 h) ── verbatim TERMINAL_MESSAGES.expired
  // Le serveur a déjà prévenu Dimitri au moment de l'expiration (sweep).
  signature_expiree: {
    titre: "Lien expiré",
    message:
      "Ce parcours de réservation a expiré. Si vous souhaitez toujours réserver, n'hésitez pas à nous recontacter.",
    ton: TON_MAUVAIS,
    geste: { type: "ecrire" },
  },

  // ── 5bis · Délai de paiement écoulé (48 h après signature) ── [D32]
  // Textes VERBATIM maquette v4 (docs/handoff/voyageur-proto.html, exception
  // `delaiFini`). DEUX expirations, DEUX messages (GO Dimitri) : le lien de
  // signature expiré (chemin 5) est un parcours MORT ; ici le contrat signé
  // reste valable, seules les dates sont reparties à la vente — elles sont
  // peut-être encore libres. Le motif est DÉRIVÉ par le serveur
  // (`expired_motif`, jamais stocké) ; au moindre doute la clé est absente et
  // l'écran générique `signature_expiree` reprend la main (fail-closed).
  // Enrichi à l'écran comme le séjour annulé : cf. `enrichirDelaiEcoule`.
  delai_ecoule: {
    titre: "Vos dates ont été libérées",
    message: "Le délai de 48 heures pour régler est passé.",
    ton: TON_ATTENTE,
    geste: { type: "ecrire" },
  },

  // ── 6 · Séjour annulé ── verbatim TERMINAL_MESSAGES.cancelled
  // Enrichi à l'écran par la disponibilité des dates + « Refaire une demande » :
  // cf. `enrichirAnnule`, qui porte les trois phrases figées (décision Dimitri
  // 2026-07-15) et le garde-fou « dates indisponibles ⇒ pas de bouton ».
  sejour_annule: {
    titre: "Séjour annulé",
    // [corrections Dimitri 2026-09-06] le titre suffit — « Ce séjour a été
    // annulé. » le répétait (même doublon que les dates prises). La phrase de
    // disponibilité (enrichirAnnule) et le bouton disent la suite.
    message: null,
    ton: TON_MAUVAIS,
    geste: { type: "ecrire" },
  },

  // ── 7 · Contrat non générable ── verbatim (repli du 503 fail-closed)
  // Clause d'annulation ou adresse du bailleur manquante : le serveur REFUSE de
  // générer et a déjà alerté Dimitri. Les dates, elles, sont conservées.
  contrat_indisponible: {
    titre: "Contrat indisponible",
    message:
      "Votre contrat est momentanément indisponible, nous revenons vers vous rapidement.",
    ton: TON_ATTENTE,
    geste: { type: "ecrire" },
  },

  // ── 8 · PDF pas prêt ── verbatim (encart + bouton Réessayer)
  pdf_pas_pret: {
    titre: "Contrat en préparation",
    message:
      "Votre contrat est en cours de préparation — réessayez dans un instant.",
    ton: TON_ATTENTE,
    geste: { type: "reessayer", libelle: "Réessayer" },
  },

  // ── 9 · Dates prises entre-temps ── verbatim (409 slot_taken)
  dates_prises: {
    titre: "Ces dates viennent d’être prises",
    // [arrêt 26 → corrections Dimitri 2026-09-06] TROIS lignes : le titre dit
    // tout (aucun sous-titre qui le répète), la phrase dit le NEUF (« rien
    // prélevé » + « une suite possible »), le bouton fait le reste. Ton
    // ATTENTE, pas alerte : « c'est une nouvelle avec une porte de sortie ».
    message: null,
    ton: TON_ATTENTE,
    rassurance: "Rien ne vous a été prélevé. D’autres dates sont peut-être libres.",
    geste: { type: "ecrire" },
  },

  // ── 10 · Plafond de tentatives ── verbatim (409 hold_refresh_limit)
  // Anti-squat : au 4ᵉ passage en caisse, le lien de paiement ne se rafraîchit
  // plus. Ce n'est pas un incident technique, c'est une garde.
  plafond_tentatives: {
    titre: "Lien de paiement expiré",
    message:
      "Votre lien de paiement a expiré après plusieurs tentatives. Contactez-nous pour réactiver votre demande.",
    ton: TON_MAUVAIS,
    geste: { type: "ecrire" },
  },

  // ── 11 · LA CARTE REFUSÉE ── [D28 — PÉRIMÉE, verdict Dimitri 2026-09-06]
  //
  // ⚠️ EN RÉSERVE, DÉBRANCHÉ EXPRÈS : monté, prouvé (tests composant : textes,
  // chrono, repli), mais AUCUN chemin n'y mène — et c'est un choix. Le refus
  // de carte se joue dans le cadre Stripe (Embedded Checkout affiche l'erreur
  // et permet de réessayer sur place) ; au retour sur l'espace, la session en
  // trois états prend le relais. « Un webhook chemin argent pour un troisième
  // message ne vaut pas son risque. » D28 demandait un écran pour un trou qui
  // n'en était pas un. Si un jour Stripe change ou que des abandons
  // apparaissent, l'écran est là — le brancher = webhook payment_failed
  // (CHEMIN ARGENT : revue adversariale + GO Dimitri).
  //
  // « Aujourd'hui rien ne s'affiche du tout — c'est le pire trou du parcours. »
  // Vérifié : une carte refusée se joue entièrement dans le module de paiement ;
  // côté FBZ rien ne s'affiche et la page ne bouge pas. Le voyageur ne sait ni
  // qu'il n'a rien payé, ni pourquoi, ni combien de temps il lui reste.
  //
  // L'ORDRE DES TROIS PHRASES EST LA DÉCISION : d'abord l'argent (« aucune
  // somme n'a été prélevée » — c'est la seule chose qui inquiète), ensuite la
  // cause probable (« un plafond dépassé ou un paiement en ligne bloqué » : la
  // banque ne nous dit jamais laquelle, on ne prétend donc pas savoir), et
  // seulement ensuite le temps qui reste.
  carte_refusee: {
    titre: "Paiement refusé",
    message: "Votre banque a refusé le paiement.",
    ton: TON_MAUVAIS,
    rassurance:
      "Aucune somme n’a été prélevée. Cela arrive souvent pour un plafond dépassé ou un paiement en ligne bloqué. Réessayez avec une autre carte, ou appelez votre banque.",
    geste: { type: "reessayer", libelle: "Réessayer le paiement" },
  },
};

/**
 * Les trois phrases de disponibilité d'un séjour annulé — FIGÉES (décision
 * Dimitri 2026-07-15), reprises telles quelles.
 *
 * `dates_available` est calculée par le serveur POUR CET ÉTAT SEULEMENT. Trois
 * valeurs, trois phrases, et surtout : quand la disponibilité est INCONNUE on
 * ne dit ni oui ni non — on invite sans promettre.
 */
function enrichirAnnule({ datesDisponibles, urlRefaire }) {
  let dispo;
  if (datesDisponibles === true) {
    dispo =
      "Bonne nouvelle : ces dates sont toujours disponibles. Vous pouvez refaire une demande de réservation.";
  } else if (datesDisponibles === false) {
    dispo =
      "Ces dates ne sont plus disponibles. Contactez-nous si vous souhaitez d’autres dates.";
  } else {
    dispo =
      "Les dates sont peut-être encore libres — n’hésitez pas à refaire une demande.";
  }
  // Le bouton ne s'affiche PAS quand les dates sont formellement prises : un
  // bouton qui mène à un refus est pire que pas de bouton.
  const geste =
    urlRefaire && datesDisponibles !== false
      ? { type: "refaire", libelle: "Refaire une demande", href: urlRefaire }
      : { type: "ecrire" };
  return { ...CHEMINS.sejour_annule, dispo, geste };
}

/**
 * La suite d'un délai de 48 h écoulé [D32] — MÊME montage que `enrichirAnnule` :
 * la disponibilité réelle est servie par le serveur POUR CET ÉTAT SEULEMENT
 * (mêmes champs `dates_available` / `redo_url` que le séjour annulé, même
 * calcul côté serveur), et le bouton ne s'affiche PAS quand les dates sont
 * formellement prises — un bouton qui mène à un refus est pire que pas de
 * bouton.
 *
 * La phrase nominale est celle de la maquette v4 (`delaiFini`), au mot près.
 * Dates formellement PRISES : « peut-être encore libres » mentirait — on
 * reprend alors la phrase FIGÉE du séjour annulé (décision Dimitri
 * 2026-07-15), déjà verbatim dans ce module.
 */
/**
 * [arrêt 28, 2026-09-06 — demande Dimitri] L'expiré GÉNÉRIQUE porte lui aussi
 * le rattrapage : « un voyageur dont le lien a expiré peut très bien reprendre
 * depuis mon site ». Mêmes garde-fous que l'annulé : dates formellement
 * prises ⇒ AUCUN bouton ; dispo inconnue ⇒ on invite sans promettre.
 * Le mot suit l'état : rien n'est payé ici → « Refaire une demande ».
 */
function enrichirSignatureExpiree({ datesDisponibles, urlRefaire }) {
  const dispo =
    datesDisponibles === false
      ? "Ces dates ne sont plus disponibles. Contactez-nous si vous souhaitez d’autres dates."
      : datesDisponibles === true
        ? "Bonne nouvelle : ces dates sont toujours disponibles. Vous pouvez refaire une demande de réservation."
        : null;
  const geste =
    urlRefaire && datesDisponibles !== false
      ? { type: "refaire", libelle: "Refaire une demande", href: urlRefaire }
      : { type: "ecrire" };
  return { ...CHEMINS.signature_expiree, dispo, geste };
}

function enrichirDelaiEcoule({ datesDisponibles, urlRefaire }) {
  // [arrêt 29, 2026-09-06 — verdict Dimitri] la certitude se DIT : « peut-être »
  // datait d'avant la dispo servie. Et « votre contrat signé reste valable »
  // vit dans les TROIS cas — c'est ce qui distingue cet écran des autres
  // expirations.
  const dispo =
    datesDisponibles === false
      ? "Votre contrat signé reste valable, mais ces dates ne sont plus disponibles. Contactez-nous si vous souhaitez d’autres dates."
      : datesDisponibles === true
        ? "Votre contrat signé reste valable. Bonne nouvelle : ces dates sont toujours disponibles."
        : "Votre contrat signé reste valable, mais les dates sont reparties à la vente. Elles sont peut-être encore libres — relancez pour vérifier.";
  const geste =
    urlRefaire && datesDisponibles !== false
      ? // « Le mot suit l'état » (Dimitri, arrêt 3) : rien n'est payé ici.
      { type: "refaire", libelle: "Relancer ma demande", href: urlRefaire }
      : { type: "ecrire" };
  return { ...CHEMINS.delai_ecoule, dispo, geste };
}

// ═══ LE CHRONO DE LA CARTE REFUSÉE ══════════════════════════════════════════
//
// LES DEUX DURÉES SONT RÉELLES, RELEVÉES AU CODE — pas inventées :
//   · la SESSION de paiement expire à NOW + 30 min
//     (api/stripe.js : `Math.floor((Date.now() + 30 * 60 * 1000) / 1000)`)
//   · le BLOCAGE DES DATES tient NOW + 35 min
//     (migration 20260807000001 : `NOW() + INTERVAL '35 minutes'`, posé et
//      rafraîchi par les deux fonctions de réservation)
// L'invariant est écrit dans la migration : « hold(35min) > session(30min) +
// 5min » — le blocage survit toujours à la session, sinon un voyageur qui paie
// à la 29ᵉ minute paierait des dates déjà revendues.
//
// LE CHRONO AFFICHE LE BLOCAGE, PAS LA SESSION. C'est le blocage qui répond à
// la question que le voyageur se pose : « est-ce que mes dates m'attendent
// encore ? ». La session, elle, se recrée d'un clic.
const MINUTES_SESSION_PAIEMENT = 30;
const MINUTES_BLOCAGE_DATES = 35;

/**
 * Le temps restant sur le blocage des dates.
 *
 * ⚠️ AUCUN REPLI, ET C'EST VOULU. Sans instant d'expiration servi, on rend
 * `null` et l'écran n'affiche PAS de chrono. Un compte à rebours faux — parce
 * que l'horloge du téléphone dérive, parce que le blocage a déjà été consommé,
 * parce que la charge est vieille de dix minutes — est pire que pas de chrono :
 * il fait patienter quelqu'un dont les dates sont déjà reparties à la vente.
 * Le texte statique « vos dates sont réservées 30 min » reste alors le repli,
 * et il ne ment sur rien.
 *
 * @param {string|null} expireLe  instant ISO du `checkout_hold_expires_at` servi
 * @param {number} maintenant     epoch ms (injecté : module pur, testable)
 * @returns {{minutes:number, secondes:number, texte:string}|null}
 */
function resteSurLeBlocage(expireLe, maintenant) {
  if (typeof expireLe !== "string" || !expireLe) return null;
  const fin = Date.parse(expireLe);
  if (!Number.isFinite(fin)) return null;
  if (!Number.isFinite(maintenant)) return null;
  const restant = fin - maintenant;
  // Déjà passé : pas de « 00:00 » qui donne l'illusion d'un dernier instant.
  if (restant <= 0) return null;
  // Au-delà du blocage nominal, la donnée est incohérente (horloge décalée,
  // charge falsifiée) : on préfère se taire.
  if (restant > MINUTES_BLOCAGE_DATES * 60 * 1000) return null;
  const totalSecondes = Math.floor(restant / 1000);
  const minutes = Math.floor(totalSecondes / 60);
  const secondes = totalSecondes % 60;
  return {
    minutes,
    secondes,
    texte: `${String(minutes).padStart(2, "0")}:${String(secondes).padStart(2, "0")}`,
  };
}

/** La phrase qui accompagne le chrono. Une seule, et elle ne promet rien. */
const TEXTE_BLOCAGE =
  "Vos dates restent réservées pendant ce temps. Vous avez le temps de réessayer.";

/** Le repli sans chrono — texte existant, inchangé. */
const TEXTE_BLOCAGE_SANS_CHRONO =
  "Vos dates sont réservées 30 min le temps du paiement. Si non finalisé, elles redeviennent disponibles — vous pourrez réessayer tant qu’elles le sont.";

// ═══ LE CHRONO DES 48 HEURES (écran « signé, à payer ») ═════════════════════
//
// Même philosophie que `resteSurLeBlocage` — un chrono qui SAIT SE TAIRE —
// mais pas la même horloge : ici c'est la caducité 48 h post-signature
// (décision Dimitri 2026-08-01, contrat v2 art. 4.3), celle que le cron
// `api/cron/release-expired-bookings.js` applique (passe `signed`). Le serveur
// sert l'échéance dérivée (`paiement_expire_le` = contract_signed_at + 48 h,
// source unique lib/direct-vars-catalog.js `PAYMENT_DEADLINE_HOURS`).
//
// MIROIR CLIENT DE LA DURÉE : src/ ne peut pas importer lib/ (ModuleScopePlugin
// CRA) — même précédent déclaré que `moneyOrNull` (JourneyPage) et
// `JOURNEY_CAUTION_WINDOW_DAYS`. Toute évolution se fait côté serveur d'abord,
// ici ensuite. La constante ne sert qu'au garde-fou de cohérence : un
// « restant » supérieur à la fenêtre entière = donnée incohérente → silence.
const HEURES_CADUCITE_PAIEMENT = 48;

/**
 * Le temps restant avant la caducité 48 h — « HH:MM » (la maquette affiche
 * « 28:14 » : des heures et des minutes, pas des secondes — personne ne
 * regarde 48 h défiler à la seconde).
 *
 * ⚠️ AUCUN REPLI, ET C'EST VOULU (même règle que `resteSurLeBlocage`) :
 *   · échéance absente / illisible → null (pas de bandeau) ;
 *   · échéance PASSÉE → null — jamais un « 00:00 » menteur : l'écran
 *     d'expiration (« Vos dates ont été libérées », D32) prend le relais au
 *     refetch ;
 *   · restant > 48 h → donnée incohérente (horloge décalée, charge forgée) →
 *     silence.
 *
 * @param {string|null} expireLe  instant ISO `paiement_expire_le` servi
 * @param {number} maintenant     epoch ms (injecté : module pur, testable)
 * @returns {{heures:number, minutes:number, texte:string}|null}
 */
function resteSurLaCaducite(expireLe, maintenant) {
  if (typeof expireLe !== "string" || !expireLe) return null;
  const fin = Date.parse(expireLe);
  if (!Number.isFinite(fin)) return null;
  if (!Number.isFinite(maintenant)) return null;
  const restant = fin - maintenant;
  if (restant <= 0) return null;
  if (restant > HEURES_CADUCITE_PAIEMENT * 60 * 60 * 1000) return null;
  const totalMinutes = Math.floor(restant / 60000);
  const heures = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return {
    heures,
    minutes,
    totalMinutes,
    // Le cadran chiffré ne sert plus qu'aux délais COURTS (session de
    // paiement, carte refusée) — le 48 h se dit en heures et en phrase
    // (Dimitri, arrêt 3 : deux délais, deux affichages).
    texte: `${String(heures).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
  };
}

/** La phrase du bandeau des 48 h — celle de la maquette, au mot près. */
const TEXTE_CADUCITE =
  "Vos dates sont réservées pendant ce temps. Passé ce délai, elles repartent à la vente.";

/**
 * Traduit un code d'erreur serveur en chemin d'exception.
 * Un code inconnu rend `null` : on ne range pas de force une situation qu'on
 * n'a pas prévue dans un écran qui la nommerait mal.
 */
const CODES_SERVEUR = {
  not_found: "lien_inconnu",
  invalid: "lien_invalide",
  network: "panne",
  rejected: "demande_non_retenue",
  expired: "signature_expiree",
  cancelled: "sejour_annule",
  hold_refresh_limit: "plafond_tentatives",
  slot_taken: "dates_prises",
  card_declined: "carte_refusee",
  payment_failed: "carte_refusee",
};

function cheminDepuisCode(code) {
  const cle = CODES_SERVEUR[code];
  return cle ? { cle, ...CHEMINS[cle] } : null;
}

function chemin(cle) {
  return CHEMINS[cle] ? { cle, ...CHEMINS[cle] } : null;
}

module.exports = {
  CHEMINS,
  chemin,
  cheminDepuisCode,
  enrichirAnnule,
  enrichirDelaiEcoule,
  enrichirSignatureExpiree,
  resteSurLeBlocage,
  resteSurLaCaducite,
  TEXTE_BLOCAGE,
  TEXTE_BLOCAGE_SANS_CHRONO,
  TEXTE_CADUCITE,
  HEURES_CADUCITE_PAIEMENT,
  TON_MAUVAIS,
  TON_ATTENTE,
  MINUTES_SESSION_PAIEMENT,
  MINUTES_BLOCAGE_DATES,
};
